import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  InputGroup,
  InputGroupText,
  CustomInput,
  FormFeedback,
  Toast,
  ToastHeader,
  ToastBody,
  Table
} from 'reactstrap';
import { FaSave, FaTimes, FaBoxes, FaBarcode, FaMoneyBillWave, FaPlus, FaMinus, FaWarehouse, FaArrowLeft, FaImage, FaUserTie } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { productActions } from '../../sagas/productSlice';
import Swal from 'sweetalert2';
import config from '../../helpers/config';
import { categoryActions } from '../../sagas/categorySlice';
import Select from 'react-select';
import { error } from 'toastr';
import { formatPrice } from '../../helpers/utility';
import { debounce } from 'lodash';

// Données de référence (à remplacer par vos appels API)
const TYPES_ARTICLE = [
  { value: 'STOCKABLE', label: 'Article stockable' },
  { value: 'SERVICE', label: 'Service' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'OTHER', label: 'Autre' }
];

const TVA_OPTIONS = [
  { code: 0.2, libelle: '20%' },
  { code: 0.1, libelle: '10%' },
  { code: 0.07, libelle: '7%' },
  { code: 0.14, libelle: '14%' }
];

// Schéma de validation
const ArticleSchema = Yup.object().shape({
  libelle: Yup.string()
    .required('Le libellé est obligatoire'),
  typeArticle: Yup.string()
    .required('Le type est obligatoire'),
  categorie: Yup.number()
    .required('La catégorie est obligatoire'),
  unite: Yup.string()
    .required("L'unité est obligatoire"),
  prixVenteTTC1: Yup.number()
    .min(0, 'Le prix doit être positif')
    .when('prixVariable', {
      is: false,
      then: (schema) => schema.required('Le prix de vente est obligatoire'),
      otherwise: (schema) => schema.notRequired()
    }),
  prixVariable: Yup.boolean()
});

const FormulaireArticle = () => {
  const { error, prices } = useSelector(state => state.products); // Assuming you have a Redux store with error state
  const [typeArticle, setTypeArticle] = useState('STOCKABLE');
  const [imagePreview, setImagePreview] = useState(null); // State for image preview
  const { t } = useTranslation(); // Assuming you are using react-i18next for translations
  const navigate = useNavigate(); // Assuming you are using react-router-dom for navigation
  const { list } = useSelector(state => state.categories); // Assuming you have a Redux store with categories
  const dispatch = useDispatch(); // Assuming you are using Redux for state management
  const location = useLocation();
  const unites = useSelector(state => state.products.unites); // Assuming you have a Redux store with unites
  const loading = useSelector(state => state.products.loading); // Assuming you have a Redux store with loading state
  const product = location.state?.product || null;
  const initialValues = {
    code: product ? product.code : '',
    codeBarre: product ? product.codeBarre : '',
    libelle: product ? product.libelle : '',
    typeArticle: product ? product.typeArticle : 'STOCKABLE',
    categorie: product ? product.categorieId : null,
    unite: product ? product.unite : null,
    prixAchatTTC: product ? product.prixAchatTTC : 0,
    tauxTVAAchat: product ? product.tauxTVAAchat : 0.20,
    tauxTVAVente: product ? product.tauxTVAVente : 0.20,
    prixVenteTTC1: product ? product.prixVenteTTC1 : 0,
    prixVenteTTC2: product ? product.prixVenteTTC2 : 0,
    prixVenteTTC3: product ? product.prixVenteTTC3 : 0,
    stockMinimal: product ? product.stockMinimal : 0,
    stockPhysique: product ? product.stockPhysique : 0,
    stockDisponible: product ? product.stockDisponible : 0,
    prixVariable: product ? product.prixVariable : false,
    enStock: product ? product.enStock : true,
    actif: product ? product.actif : true,
    photo: null
  };

  const [averagePriceHT, setAveragePriceHT] = useState(0);
  const [averagePriceTTC, setAveragePriceTTC] = useState(0);

  useEffect(() => {
    // Fetch unites when the component mounts
    dispatch(productActions.findAllUnites());
    dispatch(categoryActions.list({ query: '' })); // Fetch categories if needed

    if (product) {
      console.log('Product found in state:', product);
      dispatch(productActions.findAllPrices({ articleId: product.id }));
    }


  }, [dispatch]);

  const searchCategories = useMemo(
    () => debounce((query) => {
      dispatch(categoryActions.list({ query: query || '' }));
    }, 300),
    [dispatch]
  );

  useEffect(() => () => searchCategories.cancel(), [searchCategories]);

  useEffect(() => {
    if (prices && prices.length > 0) {
      // Calculate average HT price
      const totalHT = prices.reduce((sum, item) => sum + parseFloat(item.prixAchatHT || 0), 0);
      const avgHT = totalHT / prices.length;
      setAveragePriceHT(avgHT);

      // Calculate average TTC price using the product's TVA rate
      const avgTTC = avgHT * (1 + parseFloat(initialValues.tauxTVAAchat || 0.2));
      setAveragePriceTTC(avgTTC.toFixed(2)); // Set to 2 decimal places

      // Update the form's prixAchatTTC field if using Formik's setFieldValue
      // This would need to be done in the Formik component
    }
  }, [prices, initialValues.tauxTVAAchat]);


  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      console.log('Article à enregistrer :', values);

      // Create FormData object
      const formData = new FormData();

      // Map form values to the ArticleDTO structure
      const articleDTO = {
        code: values.code,
        libelle: values.libelle,
        typeArticle: values.typeArticle,
        codeBarre: values.codeBarre,
        categorieId: values.categorie, // Assuming `categorie` is the ID
        unite: values.unite, // Assuming `unite` is the code
        prixAchatTTC: values.prixAchatTTC,
        tauxTVAAchat: values.tauxTVAAchat,
        prixVenteTTC1: values.prixVariable ? 0 : values.prixVenteTTC1,
        prixVenteTTC2: values.prixVariable ? 0 : values.prixVenteTTC2,
        prixVenteTTC3: values.prixVariable ? 0 : values.prixVenteTTC3,
        tauxTVAVente: values.tauxTVAVente,
        stockMinimal: values.stockMinimal,
        stockPhysique: values.stockPhysique,
        stockDisponible: values.stockDisponible,
        prixVariable: values.prixVariable,
        enStock: values.enStock,
        actif: values.actif
      };

      // Add the article as a JSON string
      //formData.append('article', JSON.stringify(articleDTO));
      formData.append('article', new Blob([JSON.stringify(articleDTO)], {
        type: 'application/json'
      }));

      // Add the photo if it exists
      if (values.photo instanceof File) {
        formData.append('photo', values.photo);
      }

      // Prepare the payload
      let payload = {
        formData,
        onSuccess: () => {
          Swal.fire({
            icon: 'success',
            title: 'Succès',
            text: 'Article enregistré avec succès !',
            confirmButtonText: 'OK'
          });
          resetForm();
          setImagePreview(null);
          navigate('/inventory-module/products'); // Redirect to the articles list page
        }
      };

      // Dispatch the action to save the article
      if (product) {
        dispatch(productActions.updateProduct({ ...payload, id: product.id })); // Pass the product
      } else {
        dispatch(productActions.addProduct(payload));
      }

    } catch (error) {
      console.error('Erreur lors de l\'enregistrement :', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue lors de l\'enregistrement de l\'article.',
        confirmButtonText: 'OK'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setFieldValue('photo', file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result); // Set the image preview URL
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null); // Clear the preview if no file is selected
    }
  };

  const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
  const htFromTtc = (ttc, tva) => round2(Number(ttc || 0) / (1 + Number(tva || 0)));


  const renderSupplierPrices = () => {
    if (loading) {
      return (
        <div className="text-center my-3">
          <Spinner size="sm" color="primary" />
          <span className="ms-2">{t('common.loading', 'Loading...')}</span>
        </div>
      );
    }

    if (!prices || prices.length === 0) {
      return (
        <div className="text-muted text-center my-3">
          {t('product.noSupplierPrices')}
        </div>
      );
    }


    return (
      <Table responsive striped bordered hover size="sm" className="mt-3">
        <thead>
          <tr>
            <th>{t('supplier.entreprise')}</th>
            <th className="text-end">{t('product.purchasePriceHT', 'Purchase Price (HT)')}</th>
            <th className="text-end">{t('product.purchasePriceTTC', 'Purchase Price (TTC)')}</th>
          </tr>
        </thead>
        <tbody>
          {prices.map((item, index) => (
            <tr key={index}>
              <td>{item.fournisseurNom}</td>
              <td className="text-end">{formatPrice(item.prixAchatHT)}</td>
              <td className="text-end">
                {formatPrice(item.prixAchatHT * (1 + (initialValues.tauxTVAAchat || 0.2)))}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  // if (loading) {
  //   return (
  //     <div className="d-flex justify-content-center align-items-center vh-100">
  //       <Spinner color="primary" size="lg" />
  //     </div>
  //   );
  // }

  return (
    <Container fluid>
      {loading && (
        <div className="d-flex justify-content-center align-items-center vh-100">
          <Spinner color="primary" size="lg" />
        </div>
      )
      }

      <Row className="mb-4" >
        <Col>
          <Card className="shadow-sm">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaBoxes className="text-primary me-2" />
                {product ? t('product.editArticle') : t('product.newArticle')}
              </h4>
              <Button
                color="secondary"
                onClick={() => navigate(-1)} // Go back to the previous page
                className="d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                {t('common.return')}
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Card>
        <CardBody>
          {error && (
            <Toast className="position-fixed top-0 end-0 m-3" isOpen={true}>
              <ToastHeader icon="danger" toggle={() => dispatch(productActions.clearError())} className="bg-danger text-white">
                {t('common.error')}
              </ToastHeader>
              <ToastBody>{error}</ToastBody>
            </Toast>)}
          <Formik
            initialValues={initialValues}
            enableReinitialize={true} // Enable reinitialization with new props
            validationSchema={ArticleSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
              <Form>
                <Row>
                  {/* Column 1 - Basic Information */}
                  <Col md={3}>
                    <FormGroup>
                      <Label>Type d'article *</Label>
                      <Input
                        type="select"
                        name="typeArticle"
                        onChange={(e) => {
                          setTypeArticle(e.target.value);
                          handleChange(e);
                        }}
                        value={values.typeArticle}
                      >
                        {TYPES_ARTICLE.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Catégorie *</Label>
                      <Select
                        name="categorie"
                        options={list.map(cat => ({ value: cat.id, label: cat.libelle }))}
                        value={list.map(cat => ({ value: cat.id, label: cat.libelle }))
                          .find(option => String(option.value) === String(values.categorie)) || null}
                        onChange={(selected) => setFieldValue('categorie', selected ? selected.value : '')}
                        onInputChange={(value, actionMeta) => {
                          if (actionMeta.action === 'input-change') searchCategories(value);
                        }}
                        placeholder="Rechercher une catégorie..."
                        isClearable
                        isSearchable
                        className={touched.categorie && errors.categorie ? 'is-invalid' : ''}
                        classNamePrefix="react-select"
                      />
                      {touched.categorie && errors.categorie && (
                        <div className="text-danger small mt-1">{errors.categorie}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Unité *</Label>
                      <Select
                        name="unite"
                        options={unites.map(unite => ({
                          value: unite.code,
                          label: `${unite.libelle} (${unite.code})`
                        }))}
                        value={unites.map(unite => ({
                          value: unite.code,
                          label: `${unite.libelle} (${unite.code})`
                        })).find(option => option.value === values.unite) || null}
                        onChange={(selected) => setFieldValue('unite', selected ? selected.value : '')}
                        placeholder="Rechercher une unité..."
                        isClearable
                        isSearchable
                        className={touched.unite && errors.unite ? 'is-invalid' : ''}
                      />
                      {touched.unite && errors.unite && (
                        <div className="text-danger small mt-1">{errors.unite}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Libellé *</Label>
                      <Input
                        name="libelle"
                        value={values.libelle}
                        onChange={handleChange}
                        invalid={touched.libelle && !!errors.libelle}
                      />
                      <FormFeedback>{errors.libelle}</FormFeedback>
                    </FormGroup>
                  </Col>

                </Row>
                <Row>
                  <Col md={3}>
                    <Label>Code Barre</Label>
                    <Input
                      name="codeBarre"
                      value={values.codeBarre}
                      onChange={handleChange}
                    />
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>Prix d'achat TTC {prices && prices.length > 0 && "(Moyenne des fournisseurs)"}</Label>
                      <Input
                        type="number"
                        name="prixAchatTTC"
                        onSelect={e => e.target.select()}
                        value={prices && prices.length > 0 ? averagePriceTTC : values.prixAchatTTC}
                        onChange={handleChange}
                        readOnly={prices && prices.length > 0}
                        className={prices && prices.length > 0 ? "bg-light" : ""}
                      />
                      <small className="text-muted">
                        Prix HT: {prices && prices.length > 0
                          ? formatPrice(averagePriceHT)
                          : formatPrice(values.prixAchatTTC / (1 + values.tauxTVAAchat))}
                      </small>
                      {prices && prices.length > 0 && (
                        <small className="d-block text-info mt-1">
                          <FaUserTie className="me-1" size={12} />
                          Prix calculé à partir de {prices.length} fournisseur(s)
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md={3}>
                    <FormGroup>
                      <Label>P.V. TTC (1) *</Label>
                      <Input
                        type="number"
                        name="prixVenteTTC1"
                        onSelect={e => e.target.select()}
                        value={values.prixVenteTTC1}
                        onChange={(e) => setFieldValue('prixVenteTTC1', e.target.value)}
                      />
                      <small className="text-muted">
                        P.V. HT : {htFromTtc(values.prixVenteTTC1, values.tauxTVAVente)} DH
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>P.V. TTC (2)</Label>
                      <Input
                        type="number"
                        name="prixVenteTTC2"
                        onSelect={e => e.target.select()}
                        value={values.prixVenteTTC2}
                        onChange={(e) => setFieldValue('prixVenteTTC2', e.target.value)}
                      />
                      <small className="text-muted">
                        P.V. HT : {htFromTtc(values.prixVenteTTC2, values.tauxTVAVente)} DH
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>P.V. TTC (3)</Label>
                      <Input
                        type="number"
                        name="prixVenteTTC3"
                        onSelect={e => e.target.select()}
                        value={values.prixVenteTTC3}
                        onChange={(e) => setFieldValue('prixVenteTTC3', e.target.value)}
                      />
                      <small className="text-muted">
                        P.V. HT : {htFromTtc(values.prixVenteTTC3, values.tauxTVAVente)} DH
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label>TVA</Label>
                      <Input
                        type="select"
                        name="tauxTVAVente"
                        value={values.tauxTVAVente}
                        onChange={(e) => setFieldValue('tauxTVAVente', Number(e.target.value))}
                      >
                        {TVA_OPTIONS.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.libelle}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  {typeArticle === 'STOCKABLE' && (
                    <>
                      <Col md={3}>

                        <FormGroup>
                          <Label>Stock minimal</Label>
                          <Input
                            type="number"
                            onFocus={(e) => e.target.select()} // Select the field content on focus
                            name="stockMinimal"
                            value={values.stockMinimal}
                            onChange={handleChange}
                            step="1"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Stock physique</Label>
                          <Input
                            type="number"
                            onFocus={(e) => e.target.select()} // Select the field content on focus
                            name="stockPhysique"
                            value={values.stockPhysique}
                            onChange={(e) => {
                              handleChange(e);
                              setFieldValue('stockDisponible', e.target.value); // Update stockDisponible dynamically
                            }}
                            step="1"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Stock disponible</Label>
                          <Input
                            type="number"
                            onFocus={(e) => e.target.select()} // Select the field content on focus
                            name="stockDisponible"
                            value={values.stockDisponible}
                            onChange={handleChange}
                            disabled
                          />
                        </FormGroup>
                      </Col></>)}
                  <Col md={3}>
                    <FormGroup>
                      <Label>Photo</Label>
                      <Input
                        type="file"
                        name="photo"
                        onChange={(event) => handleImageChange(event, setFieldValue)}
                      />
                      {errors.photo && touched.photo && (
                        <div className="text-danger small mt-1">{errors.photo}</div>
                      )}
                      <div className="card-image-container">
                        {imagePreview || product?.photo ? (
                          <img
                            src={imagePreview || `${config.baseURL}${product.photo}`}
                            alt={product?.libelle || 'Aperçu'}
                            className="card-img-top rounded-top"
                            style={{ maxWidth: '160px', maxHeight: '160px', objectFit: 'contain' }}
                          />
                        ) : (
                          <div className="bg-placeholder rounded-top d-flex align-items-center justify-content-center">
                            <FaImage size={32} className="text-muted" />
                          </div>
                        )}
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                {/* Add Supplier Prices Section - Only show when editing a product */}
                {product && product.id && (
                  <Row className="mt-4">
                    <Col md={12}>
                      <Card className="border">
                        <CardHeader className="bg-light">
                          <h5 className="mb-0 d-flex align-items-center">
                            <FaUserTie className="me-2 text-primary" />
                            {t('product.supplierPrices')}
                          </h5>
                        </CardHeader>
                        <CardBody>
                          {renderSupplierPrices()}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}

                <div className="mt-4 pt-3 border-top">
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Spinner size="sm" /> Enregistrement...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" /> Enregistrer
                      </>
                    )}
                  </Button>
                  <Button color="light" className="ms-2" onClick={() => navigate(-1)}>
                    <FaTimes className="me-2" /> Annuler
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </CardBody>
      </Card>
    </Container>
  );
};

export default FormulaireArticle;
